import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { fmt2 } from "@/lib/utils";
import { useLocations } from "@/context/LocationContext";
import { useScanner } from "@/context/ScannerContext";
import {
  Factory,
  PackagePlus,
  Truck,
  AlertTriangle,
  Trash2,
  Archive,
  ChevronRight,
  Package,
  Search,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import ScannerCard from "@/components/ScannerCard";
import StockAdjustDialog from "@/components/StockAdjustDialog";

const Stat = ({ icon: Icon, label, value, accent = "text-primary" }) => (
  <div className="stat-card" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-bold">
          {label}
        </div>
        <div className="font-display text-4xl font-bold tnum mt-2">{value}</div>
      </div>
      <Icon className={`h-7 w-7 ${accent}`} />
    </div>
  </div>
);

function CleaningOverdueBanner({ compliance }) {
  if (!compliance) return null;
  const daily = compliance.overall?.daily || { total: 0, done: 0, percent: 100 };
  const weekly = compliance.overall?.weekly || { total: 0, done: 0, percent: 100 };
  const dailyOpen = Math.max(0, daily.total - daily.done);
  const weeklyOpen = Math.max(0, weekly.total - weekly.done);
  if (dailyOpen === 0 && weeklyOpen === 0) return null;
  return (
    <Link
      to="/cleaning"
      data-testid="overdue-cleaning-banner"
      className="block rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-4 hover:bg-amber-100/60 dark:hover:bg-amber-900/40 transition-colors"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-300" />
        <div className="flex-1">
          <div className="font-semibold text-amber-900 dark:text-amber-100">
            Open cleaning tasks
          </div>
          <div className="text-sm text-amber-800 dark:text-amber-200">
            {dailyOpen > 0 && (
              <>
                <span className="tnum font-bold">{dailyOpen}</span> daily ·{" "}
              </>
            )}
            {weeklyOpen > 0 && (
              <>
                <span className="tnum font-bold">{weeklyOpen}</span> weekly ·{" "}
              </>
            )}
            Compliance {daily.percent}% daily · {weekly.percent}% weekly. Open the
            cleaning page to log them.
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-amber-700" />
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [stockSearch, setStockSearch] = useState("");
  const [adjustCode, setAdjustCode] = useState(null);
  const { params, selected, locations } = useLocations();
  const scanner = useScanner();

  const load = () => {
    Promise.all([
      api.get("/dashboard/summary", { params: params() }),
      api.get("/cleaning/compliance", { params: params() }),
    ]).then(([sum, cmp]) => {
      setS(sum.data);
      setCompliance(cmp.data);
    });
  };

  useEffect(() => {
    setS(null);
    load();
    // `params` is a function created fresh each render by LocationContext;
    // listing it here would loop. `selected` already drives every value
    // `params()` reads, so it's the only meaningful dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Subscribe to global scanner events: scanning an internal batch code
  // (INT-…) OR a supplier batch opens the stock-readjust dialog so the
  // operator can type the physical count without hunting through the
  // intake list. Also recognises when the Trace/Recall or Production
  // scanners emit uppercase codes.
  const adjustRef = useRef(null);
  adjustRef.current = adjustCode;
  useEffect(() => {
    if (!scanner?.subscribe) return undefined;
    const unsub = scanner.subscribe((code) => {
      const c = String(code || "").trim();
      if (!c || c.length < 3) return;
      // Only intercept codes that could plausibly be a bakery intake code.
      // Covers INT-… (our label) and most supplier batch numbers (which
      // are typically ≥4 chars and alphanumeric). Pack labels (PK-…),
      // batch codes (BAT-…) etc. are skipped so Packing / Production
      // flows still own those scans.
      if (/^(PK|BAT|BATCH|DISP|PACK)-/i.test(c)) return;
      // Ignore repeat events while a dialog is already open.
      if (adjustRef.current) return;
      setAdjustCode(c.toUpperCase());
    });
    return unsub;
  }, [scanner]);

  if (!s)
    return (
      <div className="text-stone-500" data-testid="dashboard-loading">
        Loading dashboard…
      </div>
    );

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-bold">
            Today on the shop floor
          </div>
          <h1 className="font-display text-4xl font-bold mt-1" data-testid="dashboard-title">
            Dashboard
            {selected && (
              <span className="text-stone-500 font-normal"> · {locations.find((l)=>l.id===selected)?.name || ""}</span>
            )}
          </h1>
        </div>
        <Link
          to="/intake"
          className="h-12 px-5 rounded-lg bg-primary text-white font-semibold inline-flex items-center gap-2 hover:bg-primary/90"
          data-testid="quick-new-intake"
        >
          <PackagePlus className="h-5 w-5" /> New goods intake
        </Link>
      </div>

      <CleaningOverdueBanner compliance={compliance} />

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-5">
        <Stat icon={Factory} label="Batches today" value={s.batches_today} />
        <Stat icon={Archive} label="Open batches" value={s.open_batches} />
        <Stat icon={Truck} label="Dispatches today" value={s.dispatches_today} />
        <Stat
          icon={AlertTriangle}
          label="Near expiry"
          value={s.near_expiry}
          accent="text-amber-600"
        />
        <Stat
          icon={Trash2}
          label="Waste today"
          value={s.waste_today}
          accent="text-rose-700"
        />
      </div>

      <ScannerCard />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="panel">
          <div className="panel-header">
            <div className="font-display font-semibold">Recent batches</div>
            <Link to="/production" className="text-sm text-primary inline-flex items-center gap-1">
              View all <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="panel-body">
            {s.recent_batches.length === 0 ? (
              <Empty text="No production batches yet" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-stone-500">
                    <th className="py-2">Code</th>
                    <th>Recipe</th>
                    <th>Stage</th>
                    <th className="text-right">Planned</th>
                  </tr>
                </thead>
                <tbody>
                  {s.recent_batches.map((b) => (
                    <tr key={b.id} className="border-t border-border h-12">
                      <td className="font-mono text-sm">{b.batch_code}</td>
                      <td>{b.recipe_name}</td>
                      <td>
                        <StageBadge stage={b.stage} />
                      </td>
                      <td className="text-right tnum">{fmt2(b.quantity_planned)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="panel">
          <div className="panel-header">
            <div className="font-display font-semibold">Near expiry stock</div>
            <Link to="/intake" className="text-sm text-primary inline-flex items-center gap-1">
              Goods intake <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="panel-body">
            {s.near_expiry_rows.length === 0 ? (
              <Empty text="Nothing expiring in the next 7 days" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-stone-500">
                    <th className="py-2">Material</th>
                    <th>Supplier</th>
                    <th>Batch</th>
                    <th className="text-right">Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {s.near_expiry_rows.map((i) => (
                    <tr key={i.id} className="border-t border-border h-12">
                      <td>{i.material_name}</td>
                      <td className="text-stone-600 text-sm">{i.supplier_name}</td>
                      <td className="font-mono text-sm">{i.supplier_batch}</td>
                      <td className="text-right tnum text-amber-700 font-semibold">
                        {i.expiry_date?.slice(0, 10)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {s.low_stock.length > 0 && (
        <div className="panel border-amber-300">
          <div className="panel-header bg-amber-50/80">
            <div className="font-display font-semibold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" /> Low stock alerts
            </div>
          </div>
          <div className="panel-body grid grid-cols-1 md:grid-cols-3 gap-4">
            {s.low_stock.map((m) => (
              <div key={m.id} className="border border-amber-300 rounded-lg p-4 bg-amber-50/40">
                <div className="font-semibold">{m.name}</div>
                <div className="text-sm text-stone-600">
                  Stock {fmt2(m.stock)} {m.unit} · Min {fmt2(m.min_stock)} {m.unit}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <StockCountPanel
        rows={s.materials_stock || []}
        search={stockSearch}
        onSearch={setStockSearch}
      />

      <StockAdjustDialog
        scannedCode={adjustCode}
        onClose={() => setAdjustCode(null)}
        onSaved={load}
      />
    </div>
  );
}

function StockCountPanel({ rows, search, onSearch }) {
  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.category || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <div className="panel" data-testid="stock-count-panel">
      <div className="panel-header flex-wrap gap-2">
        <div className="font-display font-semibold flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" /> Raw material stock
          <span className="text-sm text-stone-500 font-normal">
            · {filtered.length} of {rows.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search material or category"
              className="h-10 pl-9 w-56"
              data-testid="stock-count-search"
            />
          </div>
          <Link
            to="/intake"
            className="text-sm text-primary inline-flex items-center gap-1"
          >
            Goods intake <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      <div className="panel-body">
        {filtered.length === 0 ? (
          <Empty text={rows.length === 0 ? "No materials recorded yet" : "No matches"} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="stock-count-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-stone-500">
                  <th className="py-2">Material</th>
                  <th>Category</th>
                  <th className="text-right">Stock</th>
                  <th className="text-right">Min</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => {
                  const low = (m.min_stock || 0) > 0 && m.stock < m.min_stock;
                  const zero = (m.stock || 0) <= 0;
                  const overdraft = m.overdraft > 0;
                  return (
                    <tr
                      key={m.id}
                      className="border-t border-border h-12"
                      data-testid={`stock-row-${m.id}`}
                    >
                      <td className="font-medium">{m.name}</td>
                      <td className="text-stone-600 text-sm">
                        {m.category || <span className="text-stone-400">—</span>}
                      </td>
                      <td
                        className={`text-right tnum font-semibold ${
                          zero
                            ? "text-rose-700"
                            : low
                              ? "text-amber-700"
                              : "text-stone-800 dark:text-stone-100"
                        }`}
                      >
                        {fmt2(m.stock)} {m.unit}
                        {overdraft && (
                          <div
                            className="text-[10px] font-normal text-rose-600 mt-0.5"
                            title={`Overdraft of ${fmt2(m.overdraft)} ${m.unit} will clear on next goods intake`}
                            data-testid={`stock-overdraft-${m.id}`}
                          >
                            overdraft {fmt2(m.overdraft)} {m.unit}
                          </div>
                        )}
                      </td>
                      <td className="text-right tnum text-stone-500 text-sm">
                        {m.min_stock > 0 ? `${fmt2(m.min_stock)} ${m.unit}` : "—"}
                      </td>
                      <td className="text-right">
                        {low && (
                          <span className="pill-stage bg-amber-100 text-amber-900">
                            low
                          </span>
                        )}
                        {zero && !low && (
                          <span className="pill-stage bg-rose-100 text-rose-900">
                            empty
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3 text-[11px] text-stone-500 border-t border-border pt-3 flex items-center gap-2">
          <span className="font-bold uppercase tracking-wider">Tip</span>
          Scan an internal batch label (INT-…) with the connected scanner to
          readjust that intake's remaining stock. Any overdraft is auto-cleared
          when the next goods intake for that material arrives.
        </div>
      </div>
    </div>
  );
}

export function StageBadge({ stage }) {
  const map = {
    mixing: "bg-sky-100 text-sky-800",
    baking: "bg-amber-100 text-amber-900",
    packed: "bg-violet-100 text-violet-900",
    complete: "bg-emerald-100 text-emerald-900",
  };
  return (
    <span className={`pill-stage ${map[stage] || "bg-stone-100 text-stone-700"}`}>
      {stage || "—"}
    </span>
  );
}

function Empty({ text }) {
  return <div className="text-sm text-stone-500 py-6 text-center">{text}</div>;
}
